<!--
  AssigneePicker — G4.3b widget parity for the admin AssigneePickerComponent.

  Popover that lists eligible operators for the conversation's tenant +
  an explicit "Unassigned" row at top. Single-click commit: clicking a
  row immediately PUTs /api/conversations/:id/assignee and self-closes
  on success. No save button.

  Anchored to the trigger via viewport-fixed positioning, like TagPicker,
  so it escapes parent overflow clipping. Self-closes on outside-click
  or Escape.

  Wired from CustomerContextPanel (G4.3c) — host owns open/closed state.
-->
<script lang="ts">
  import { onMount, onDestroy } from 'svelte';
  import { createMutation, createQuery, useQueryClient } from '@tanstack/svelte-query';
  import { assignableOperators } from '../lib/api/queries.js';
  import { setAssigneeMutation } from '../lib/api/mutations.js';

  type Props = {
    conversationId: string;
    /** Tenant of the conversation — required to fetch eligible candidates. */
    tenantId: string;
    /** Currently-assigned operator userId (null = unassigned). Drives the ✓ marker. */
    currentAssigneeUserId: string | null;
    onClose: () => void;
    /**
     * Trigger element to anchor against. With `position: fixed` the
     * picker doesn't follow any DOM ancestor — top/right computed from
     * the anchor's getBoundingClientRect on mount + on resize.
     */
    anchor?: HTMLElement | null;
  };
  let { conversationId, tenantId, currentAssigneeUserId, onClose, anchor = null }: Props =
    $props();

  const queryClient = useQueryClient();
  const candidatesQuery = $derived(createQuery(assignableOperators(tenantId)));
  const setAssignee = $derived(createMutation(setAssigneeMutation(queryClient, conversationId)));

  let errorMessage = $state<string | null>(null);

  function initials(name: string): string {
    const trimmed = name.trim();
    if (trimmed.length === 0) return '?';
    const parts = trimmed.split(/\s+/);
    if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
    return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
  }

  async function pick(userId: string | null) {
    // Clicking the row that's already selected is a no-op — the server
    // would no-op too, but skip the round-trip.
    if (userId === currentAssigneeUserId) {
      onClose();
      return;
    }
    errorMessage = null;
    try {
      await $setAssignee.mutateAsync({ userId });
      onClose();
    } catch (err) {
      errorMessage = err instanceof Error ? err.message : 'Unknown error.';
    }
  }

  // ---- outside-click + Escape -----------------------------------------

  let pickerEl: HTMLDivElement | null = $state(null);

  function handleDocClick(event: MouseEvent) {
    const target = event.target as Node | null;
    if (!target) return;
    if (pickerEl?.contains(target)) return;
    onClose();
  }
  function handleKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape') onClose();
  }

  /**
   * Pin the picker's top-right corner relative to the anchor's bottom.
   * Uses the picker's MEASURED height for the flip-upward check (with a
   * conservative fallback before the catalog has rendered) — mirrors the
   * admin picker fix where the estimate-only check fired too eagerly.
   */
  function positionTo(el: HTMLElement) {
    if (!pickerEl) return;
    const rect = el.getBoundingClientRect();
    const pickerWidth = 260;
    const gap = 6;
    const pickerHeight = pickerEl.offsetHeight || 240;
    const right = Math.max(8, window.innerWidth - rect.right);
    let top = rect.bottom + gap;
    if (top + pickerHeight > window.innerHeight - 8) {
      top = Math.max(8, rect.top - gap - pickerHeight);
    }
    pickerEl.style.top = `${top}px`;
    pickerEl.style.right = `${right}px`;
    pickerEl.style.width = `${pickerWidth}px`;
  }

  function handleResize() {
    if (anchor) positionTo(anchor);
  }

  onMount(() => {
    document.addEventListener('click', handleDocClick);
    document.addEventListener('keydown', handleKeydown);
    window.addEventListener('resize', handleResize);
    if (anchor) {
      positionTo(anchor);
      // Re-measure after the candidate list lays out so the flip-up
      // decision uses real dimensions, not the 240px estimate.
      requestAnimationFrame(() => {
        if (anchor) positionTo(anchor);
      });
    }
  });
  onDestroy(() => {
    document.removeEventListener('click', handleDocClick);
    document.removeEventListener('keydown', handleKeydown);
    window.removeEventListener('resize', handleResize);
  });
</script>

<div class="picker" role="dialog" aria-label="Assign conversation" bind:this={pickerEl}>
  <header class="picker-header">
    <strong>Assign to</strong>
    <button
      type="button"
      class="close-btn"
      aria-label="Close assignee picker"
      onclick={onClose}
    >
      ×
    </button>
  </header>

  {#if $candidatesQuery.isPending}
    <div class="empty" role="status">Loading...</div>
  {:else if $candidatesQuery.error}
    <div class="empty error">Could not load operators.</div>
  {:else}
    <ul class="catalog" role="listbox" aria-label="Operators">
      <li role="option" aria-selected={currentAssigneeUserId === null}>
        <button
          type="button"
          class="row unassign-row"
          disabled={$setAssignee.isPending}
          onclick={() => void pick(null)}
        >
          <span class="avatar avatar-empty" aria-hidden="true">–</span>
          <span class="name">Unassigned</span>
          {#if currentAssigneeUserId === null}
            <span class="check" aria-hidden="true">✓</span>
          {/if}
        </button>
      </li>
      {#each $candidatesQuery.data ?? [] as op (op.userId)}
        <li role="option" aria-selected={currentAssigneeUserId === op.userId}>
          <button
            type="button"
            class="row"
            disabled={$setAssignee.isPending}
            onclick={() => void pick(op.userId)}
          >
            <span class="avatar" aria-hidden="true">{initials(op.displayName)}</span>
            <span class="name">{op.displayName}</span>
            {#if currentAssigneeUserId === op.userId}
              <span class="check" aria-hidden="true">✓</span>
            {/if}
          </button>
        </li>
      {/each}
      {#if ($candidatesQuery.data ?? []).length === 0}
        <li class="empty">
          <p>No operators available.</p>
          <p class="hint">Ask a tenant admin to invite teammates.</p>
        </li>
      {/if}
    </ul>
  {/if}

  {#if errorMessage}
    <div class="error-bar" role="alert">{errorMessage}</div>
  {/if}
</div>

<style>
  .picker {
    position: fixed;
    top: 0;
    right: 0;
    width: 260px;
    z-index: 50;
    background: var(--widget-bg-elevated, oklch(99.5% 0.003 250));
    border: 1px solid var(--widget-border, oklch(92% 0.005 250));
    border-radius: var(--widget-radius, 8px);
    box-shadow:
      0 1px 2px oklch(20% 0.01 250 / 0.06),
      0 10px 28px oklch(20% 0.01 250 / 0.14);
    font-family: var(--widget-font);
    font-size: 12.5px;
    color: var(--widget-fg, oklch(20% 0.01 250));
    overflow: hidden;
  }

  .picker-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 10px;
    border-bottom: 1px solid var(--widget-divider, oklch(93% 0.006 250));
  }
  .picker-header strong {
    font-size: 12.5px;
    font-weight: 600;
  }
  .close-btn {
    background: none;
    border: none;
    font-size: 18px;
    line-height: 1;
    padding: 0 4px;
    cursor: pointer;
    color: var(--widget-fg-muted, oklch(52% 0.012 250));
  }
  .close-btn:hover {
    color: var(--widget-fg, oklch(20% 0.01 250));
  }
  .close-btn:focus-visible {
    outline: 2px solid var(--widget-accent, oklch(75% 0.165 75));
    outline-offset: 1px;
    border-radius: 4px;
  }

  .empty {
    padding: 14px 12px;
    text-align: center;
    color: var(--widget-fg-muted, oklch(52% 0.012 250));
  }
  .empty.error {
    color: var(--widget-danger, oklch(55% 0.2 25));
  }
  .empty .hint {
    margin: 4px 0 0;
    font-size: 11.5px;
    color: var(--widget-fg-dim, oklch(64% 0.01 250));
  }

  .catalog {
    list-style: none;
    margin: 0;
    padding: 4px 0;
    max-height: 280px;
    overflow-y: auto;
  }
  .row {
    display: grid;
    grid-template-columns: auto 1fr auto;
    align-items: center;
    gap: 10px;
    width: 100%;
    padding: 7px 10px;
    background: none;
    border: none;
    text-align: left;
    font: inherit;
    color: inherit;
    cursor: pointer;
  }
  .row:hover:not(:disabled) {
    background: color-mix(in oklch, var(--widget-fg, oklch(20% 0.01 250)) 4%, transparent);
  }
  .row:disabled {
    cursor: default;
    opacity: 0.5;
  }
  .row:focus-visible {
    outline: 2px solid var(--widget-accent, oklch(75% 0.165 75));
    outline-offset: -1px;
  }
  .unassign-row .name {
    font-style: italic;
    color: var(--widget-fg-muted, oklch(52% 0.012 250));
  }
  .avatar {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: var(--widget-accent, oklch(75% 0.165 75));
    color: var(--widget-fg, oklch(20% 0.01 250));
    font-size: 10.5px;
    font-weight: 700;
    letter-spacing: 0.02em;
  }
  .avatar-empty {
    background: color-mix(in oklch, var(--widget-fg, oklch(20% 0.01 250)) 8%, transparent);
    color: var(--widget-fg-muted, oklch(52% 0.012 250));
    font-weight: 600;
  }
  .name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .check {
    color: var(--widget-accent-deep, oklch(50% 0.19 65));
    font-weight: 700;
    font-size: 13px;
  }

  .error-bar {
    padding: 8px 10px;
    background: var(--widget-danger-tint, oklch(94% 0.04 25));
    color: var(--widget-danger, oklch(55% 0.2 25));
    font-size: 11.5px;
    border-top: 1px solid var(--widget-danger, oklch(55% 0.2 25));
  }
</style>
